import { ActivationState } from '../../EmailVerification'
import { Domains } from '@dtx-company/true-common/src/types/domains'
import { EMAIL_VERIFICATION_COPY } from '../../../constants/emailVerification'
import {
  INCORRECT_EMAIL_OR_PASSWORD,
  USER_ALREADY_EXISTS,
  USER_IS_NOT_VERIFIED
} from '@dtx-company/ithaca-sdk/src'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { matchesRoute } from '@dtx-company/true-common/src/utils/urls/matchesRoute'
import { type SetStateAction, useCallback } from 'react'
import { clearAuthFormState } from '../../../redux/slices/auth-form'
import { fireAnalyticsEvent } from '../../../event-tracking/helpers/fireAnalyticsEvent'
import { logger } from '@dtx-company/logger'
import { useDispatch } from 'react-redux'
import { useHandleFlowcodeUserRedirect } from '../../../hooks/useHandleFlowcodeUserRedirect'
import { useIsAccountPaused } from '../../../hooks/useIsAccountPaused'
import { useLogin } from '../../../hooks/auth/useLogin'
import { useRedirectToPausedAccountPage } from '../../../hooks/useRedirectToPausedAccountPage'
import { useSignup } from '../../../hooks/auth/useSignup'
import type { AuthenticationFormFields } from '../AuthenticationForm.types'
import { useFlowRouter } from '../../../hooks/useFlowRouter'
import { usePathname } from 'next/navigation'
import { useCreateCodeAfterAuthRedirect } from '@dtx-company/app/hooks/auth-redirect/asset-creation/useCreateCodeAfterAuthRedirect'

export interface OnAuthFormSubmitProps {
  values: AuthenticationFormFields
  setErrorAlert: (value: string[]) => void
  handleGeneratorCodeCreation: () => Promise<void>
  setShowActivationBanner: (value: SetStateAction<ActivationState | null>) => void
  setIsSubmitDisabled: (value: SetStateAction<boolean>) => void
  setResendEmail: (value: SetStateAction<string | undefined>) => void
}

export function useOnAuthFormSubmit(): ({
  values,
  setErrorAlert,
  handleGeneratorCodeCreation,
  setShowActivationBanner,
  setIsSubmitDisabled,
  setResendEmail
}: OnAuthFormSubmitProps) => Promise<void> {
  const dispatch = useDispatch()
  const login = useLogin()
  const signup = useSignup()
  const { getString } = useFlowRouter()
  const pathname = usePathname() ?? ''
  const isAccountPaused = useIsAccountPaused()
  const redirectToPausedAccountPage = useRedirectToPausedAccountPage()
  const handleFlowcodeUserRedirect = useHandleFlowcodeUserRedirect()
  const createCode = useCreateCodeAfterAuthRedirect()

  const isSignIn = matchesRoute(pathname, Routes.SIGN_IN)
  const isSignUp = matchesRoute(pathname, Routes.SIGN_UP)
  const isFreeQrGeneratorPage = matchesRoute(pathname, Routes.FREE_QR_CODE_GENERATOR)
  const redirectTo = decodeURIComponent(getString('redirectTo') || '')
  const isSignupRedirectToInvite =
    (isSignUp && redirectTo.includes('accept-invite') && redirectTo.includes('invitationId')) ||
    redirectTo.includes('accept-org-invite')

  return useCallback(
    async ({
      values,
      setErrorAlert,
      handleGeneratorCodeCreation,
      setShowActivationBanner,
      setIsSubmitDisabled,
      setResendEmail
    }) => {
      // isSubmitDisabled only active in tandem w/ email verification to prevent email spam, 30s lockout
      try {
        let foundAccessToken = ''
        if (isSignUp) {
          const { user, errors, accessToken } = await signup({
            values,
            product: Domains.FLOWCODE,
            shouldForceActivate: isSignupRedirectToInvite
          })

          if (accessToken) {
            foundAccessToken = accessToken
          }

          // signup success, needs verified, green banner
          if (user.shouldActivate) {
            setIsSubmitDisabled(true)
            setShowActivationBanner(ActivationState.SIGNUP)
            fireAnalyticsEvent('userViewedActivationStateSignup', {
              copyShownToUser: EMAIL_VERIFICATION_COPY.signup
            })
          }
          // auth success, needs verified, yellow banner
          else if (errors.includes(USER_IS_NOT_VERIFIED)) {
            setIsSubmitDisabled(true)
            setShowActivationBanner(ActivationState.EXISTING_USER)
            fireAnalyticsEvent('userViewedActivationStateExistingUser', {
              copyShownToUser: EMAIL_VERIFICATION_COPY.existingUser
            })
          }
          // auth failure, special combo error instead of 2 frontegg errors
          else if (
            errors.includes(USER_ALREADY_EXISTS) &&
            errors.includes(INCORRECT_EMAIL_OR_PASSWORD)
          ) {
            const productRequestedTruncatedError = ['Incorrect email or password']
            setErrorAlert(productRequestedTruncatedError)
            fireAnalyticsEvent('userViewedAuthenticationFormError', {
              copyShownToUser: productRequestedTruncatedError
            })
          }
          // auth failure, pipe any frontegg errors as they are the same as builder login
          else if (
            errors.length > 0 &&
            !(errors.length === 1 && errors.includes(USER_ALREADY_EXISTS))
          ) {
            setErrorAlert(errors)
            fireAnalyticsEvent('userViewedAuthenticationFormError', {
              copyShownToUser: errors
            })
          }
        }

        if (isSignIn) {
          const { errors, accessToken } = await login({
            values
          })

          if (accessToken) {
            foundAccessToken = accessToken
          }
          // auth success, needs verified, yellow banner
          if (errors.includes(USER_IS_NOT_VERIFIED)) {
            setIsSubmitDisabled(true)
            setShowActivationBanner(ActivationState.EXISTING_USER)
            fireAnalyticsEvent('userViewedActivationStateExistingUser', {
              copyShownToUser: EMAIL_VERIFICATION_COPY.existingUser
            })
          }
          // auth failure, pipe any frontegg errors as they are the same as builder login
          else if (errors.length > 0) {
            setErrorAlert(errors)
            fireAnalyticsEvent('userViewedAuthenticationFormError', {
              copyShownToUser: errors
            })
          }
        }

        if (isFreeQrGeneratorPage) {
          const { user, errors, accessToken } = await signup({
            values,
            product: Domains.FLOWCODE
          })

          if (accessToken) {
            foundAccessToken = accessToken
          }

          // signup success, needs verified, green banner
          if (user.shouldActivate && user.email) {
            setIsSubmitDisabled(true)
            setResendEmail(user.email)
            setShowActivationBanner(ActivationState.LOCG_SIGNUP)
            fireAnalyticsEvent('userViewedActivationStateLOCGSignup', {
              copyShownToUser: EMAIL_VERIFICATION_COPY.locgSignup
            })
          }
          // auth success, needs verified, yellow banner
          else if (errors.includes(USER_IS_NOT_VERIFIED)) {
            setIsSubmitDisabled(true)
            setShowActivationBanner(ActivationState.EXISTING_USER)
            fireAnalyticsEvent('userViewedActivationStateExistingUser', {
              copyShownToUser: EMAIL_VERIFICATION_COPY.existingUser
            })
          }
          // auth failure, special combo error instead of 2 frontegg errors
          else if (
            errors.includes(USER_ALREADY_EXISTS) &&
            errors.includes(INCORRECT_EMAIL_OR_PASSWORD)
          ) {
            const productRequestedTruncatedError = ['Incorrect email or password']
            setErrorAlert(productRequestedTruncatedError)
            fireAnalyticsEvent('userViewedAuthenticationFormError', {
              copyShownToUser: productRequestedTruncatedError
            })
          }
          // auth failure, pipe any frontegg errors as they are the same as builder login
          else if (errors.length > 0) {
            setErrorAlert(errors)
            fireAnalyticsEvent('userViewedAuthenticationFormError', {
              copyShownToUser: errors
            })
          }
        }

        if (foundAccessToken) {
          await createCode(foundAccessToken)
          // all client post signin redirects
          if (isAccountPaused) {
            redirectToPausedAccountPage()
          } else {
            handleFlowcodeUserRedirect()
          }
        }
      } catch (e) {
        logger.error({
          name: 'Auth form submission error',
          message: e,
          technicalArea: 'authentication',
          stack: e.stack
        })
      } finally {
        dispatch(clearAuthFormState())
      }
    },
    [
      dispatch,
      isFreeQrGeneratorPage,
      isSignIn,
      isSignUp,
      login,
      signup,
      isAccountPaused,
      redirectToPausedAccountPage,
      handleFlowcodeUserRedirect,
      isSignupRedirectToInvite,
      createCode
    ]
  )
}
